import React, { Component } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Alert,
  Badge,
  Table,
  Progress
} from 'reactstrap';
import { useAuth } from '../contexts/AuthContext';
import ProtectedRoute from './ProtectedRoute';

// Wrapper component to use hooks in class component with protection
const HomeWithAuth = (props) => {
  const auth = useAuth();
  return (
    <ProtectedRoute requiredRoles={["User", "Manager", "Admin"]}>
      <Home {...props} auth={auth} />
    </ProtectedRoute>
  );
};

export class Home extends Component {
  static displayName = Home.name;

  constructor(props) {
    super(props);
    this.state = {
      analytics: null,
      loading: true,
      error: null,
      currentMonth: new Date().getMonth() + 1,
      currentYear: new Date().getFullYear()
    };
  }

  async componentDidMount() {
    await this.loadAnalytics();
  }

  loadAnalytics = async () => {
    try {
      this.setState({ loading: true });
      const { currentYear, currentMonth } = this.state;
      const response = await this.props.auth.authenticatedFetch(
        `/api/analytics/monthly?year=${currentYear}&month=${currentMonth}`
      );

      if (response && response.ok) {
        const analytics = await response.json();
        this.setState({ analytics, loading: false });
      } else if (response) {
        this.setState({ error: 'Не удалось загрузить аналитику', loading: false });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети', loading: false });
    }
  };

  formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(amount);
  };



  renderAnalytics = () => {
    const { analytics, loading, error } = this.state;

    if (loading) {
      return (
        <Container className="mt-5">
          <div className="text-center">Загрузка аналитики...</div>
        </Container>
      );
    }

    if (error) {
      return (
        <Container className="mt-5">
          <Alert color="danger">{error}</Alert>
        </Container>
      );
    }

    if (!analytics) {
      return (
        <Container className="mt-5">
          <div className="text-center text-muted">Нет данных для отображения</div>
        </Container>
      );
    }

    return (
      <Container className="mt-5">
        <Row>
          <Col>
            <h2 className="mb-4">📊 Аналитика за {analytics.monthName}</h2>
          </Col>
        </Row>

        {/* Main Metrics */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="border-success">
              <CardBody className="text-center">
                <h3 className="text-success">{this.formatCurrency(analytics.totalAmount)}</h3>
                <p className="mb-0">💰 Общая сумма актов</p>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-info">
              <CardBody className="text-center">
                <h3 className="text-info">{analytics.totalHours.toFixed(1)} ч</h3>
                <p className="mb-0">⏱️ Общее время</p>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-warning">
              <CardBody className="text-center">
                <h3 className="text-warning">{analytics.totalActs}</h3>
                <p className="mb-0">📋 Всего актов</p>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-primary">
              <CardBody className="text-center">
                <h3 className="text-primary">{analytics.activeEmployees}</h3>
                <p className="mb-0">👥 Активных сотрудников</p>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          {/* Status Distribution */}
          <Col md={12}>
            <Card className="mb-4">
              <CardHeader>
                <h5 className="mb-0">🔄 Статусы актов</h5>
              </CardHeader>
              <CardBody>
                <div className="mb-3">
                  <div className="d-flex justify-content-between">
                    <span>Черновики</span>
                    <Badge color="secondary">{analytics.statusDistribution.draft}</Badge>
                  </div>
                  <Progress value={(analytics.statusDistribution.draft / analytics.totalActs) * 100} color="secondary" className="mb-2" />
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between">
                    <span>Сформированы</span>
                    <Badge color="primary">{analytics.statusDistribution.formed}</Badge>
                  </div>
                  <Progress value={(analytics.statusDistribution.formed / analytics.totalActs) * 100} color="primary" className="mb-2" />
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between">
                    <span>Оплачены</span>
                    <Badge color="success">{analytics.statusDistribution.paid}</Badge>
                  </div>
                  <Progress value={(analytics.statusDistribution.paid / analytics.totalActs) * 100} color="success" />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Work Categories */}
        <Row>
          <Col>
            <Card>
              <CardHeader>
                <h5 className="mb-0">📁 Распределение по видам работ</h5>
              </CardHeader>
              <CardBody className="p-0">
                <Table className="mb-0">
                  <thead>
                    <tr>
                      <th>📁 Вид работ</th>
                      <th>💰 Сумма</th>
                      <th>⏱️ Часы</th>
                      <th>📝 Записей</th>
                      <th>📊 Доля</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.workCategoriesBreakdown.map((category, index) => (
                      <tr key={category.categoryName}>
                        <td>{category.categoryName}</td>
                        <td className="text-success fw-bold">
                          {this.formatCurrency(category.totalAmount)}
                        </td>
                        <td>{category.totalHours.toFixed(1)} ч</td>
                        <td>{category.recordsCount}</td>
                        <td>
                          <Progress
                            value={(category.totalAmount / analytics.totalAmount) * 100}
                            color="info"
                            style={{ height: '20px' }}
                          >
                            {((category.totalAmount / analytics.totalAmount) * 100).toFixed(1)}%
                          </Progress>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  };

  render() {
    return this.renderAnalytics();
  }
}

export default HomeWithAuth;
